// Small, safe Markdown renderer for blog articles (same file in VTCREACT).
// Everything is HTML-escaped first; only these are turned into tags:
// ## / ### headings, paragraphs, **bold**, *italic*, `code`, [links](url),
// ![images](url), - / 1. lists, > quotes, --- rules and | tables |. Links and images must be
// http(s) or site-relative, so article text can't inject scripts.

const escape = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const safeUrl = (url: string) => (/^(https?:\/\/|\/(?!\/)|#)/i.test(url.trim()) ? url.trim() : "#");

const inline = (text: string, resolve: (src: string) => string) =>
  escape(text)
    .replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_, alt, src) => `<img src="${escape(resolve(safeUrl(src)))}" alt="${alt}" loading="lazy" />`)
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label, href) => {
      const url = safeUrl(href);
      const external = /^https?:/i.test(url);
      return `<a href="${escape(url)}"${external ? ' target="_blank" rel="noopener noreferrer"' : ""}>${label}</a>`;
    })
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*\s][^*]*)\*/g, "$1<em>$2</em>");

// Tables: a "| a | b |" line followed by "| --- | :---: |" (":" sets the alignment)
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_SEPARATOR = /^\s*\|(\s*:?-{3,}:?\s*\|)+\s*$/;
// Splits "| a | b \\| c |" into cells ("\\|" is a literal pipe inside a cell)
const cellsOf = (row: string) =>
  row
    .trim()
    .replace(/^\||\|$/g, "")
    .split(/(?<!\\)\|/)
    .map((c) => c.trim().replace(/\s*\n\s*/g, "\n").replace(/\\\|/g, "|"));

function renderTable(rows: string[], resolve: (src: string) => string): string {
  const align = cellsOf(rows[1]).map((c) => (c.startsWith(":") && c.endsWith(":") ? "center" : c.endsWith(":") ? "right" : ""));
  const cell = (tag: "th" | "td", text: string, i: number) =>
    `<${tag}${align[i] ? ` style="text-align:${align[i]}"` : ""}>${inline(text, resolve).replace(/\n/g, "<br />")}</${tag}>`;
  const head = cellsOf(rows[0]);
  const body = rows.slice(2).map((r) => {
    const cells = cellsOf(r);
    // Every row gets as many cells as the header (missing ones are empty, extra ones dropped)
    return `<tr>${head.map((_, i) => cell("td", cells[i] ?? "", i)).join("")}</tr>`;
  });
  // The wrapper scrolls sideways on narrow screens instead of the whole page
  // A header row left empty (table inserted without one) is not shown
  const thead = head.some(Boolean) ? `<thead><tr>${head.map((h, i) => cell("th", h, i)).join("")}</tr></thead>` : "";
  return `<div class="table-wrap"><table>${thead}<tbody>${body.join("")}</tbody></table></div>`;
}

/** Markdown -> HTML string. `resolve` turns "/uploads/…" into a full URL. */
export function renderMarkdown(md: string, resolve: (src: string) => string = (s) => s): string {
  const out: string[] = [];
  const lines = String(md || "")
    .replace(/\r\n?/g, "\n")
    .split("\n");
  let para: string[] = [];
  let list: { tag: "ul" | "ol"; items: string[] } | null = null;
  const flushPara = () => {
    if (para.length) out.push(`<p>${inline(para.join(" "), resolve)}</p>`);
    para = [];
  };
  const flushList = () => {
    if (list) out.push(`<${list.tag}>${list.items.map((i) => `<li>${inline(i, resolve)}</li>`).join("")}</${list.tag}>`);
    list = null;
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trimEnd();
    let m: RegExpMatchArray | null;
    if (TABLE_ROW.test(line) && TABLE_SEPARATOR.test(lines[i + 1] ?? "")) {
      flushPara();
      flushList();
      // Rows: a line starting with "|" begins one; a row not yet closed by "|" goes on
      // over the next lines (a line break inside a cell). A blank line ends the table.
      const rows = [line, lines[i + 1]];
      let end = i + 2;
      while (end < lines.length && lines[end].trim()) {
        const next = lines[end].trim();
        const open = !/(?<!\\)\|$/.test(rows[rows.length - 1].trim());
        if (rows.length > 2 && open) rows[rows.length - 1] += "\n" + next;
        else if (next.startsWith("|")) rows.push(next);
        else break;
        end++;
      }
      out.push(renderTable(rows, resolve));
      i = end - 1;
    } else if (!line.trim()) {
      flushPara();
      flushList();
    } else if ((m = line.match(/^(#{2,4})\s+(.*)$/))) {
      flushPara();
      flushList();
      const level = m[1].length;
      out.push(`<h${level}>${inline(m[2], resolve)}</h${level}>`);
    } else if (/^---+$/.test(line.trim())) {
      flushPara();
      flushList();
      out.push("<hr />");
    } else if ((m = line.match(/^>\s?(.*)$/))) {
      flushPara();
      flushList();
      out.push(`<blockquote>${inline(m[1], resolve)}</blockquote>`);
    } else if ((m = line.match(/^\s*[-*]\s+(.*)$/)) || (m = line.match(/^\s*\d+[.)]\s+(.*)$/))) {
      flushPara();
      const tag = /^\s*\d/.test(line) ? "ol" : "ul";
      if (!list || list.tag !== tag) {
        flushList();
        list = { tag, items: [] };
      }
      list.items.push(m[1]);
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara();
  flushList();
  return out.join("\n");
}
